import { placementAspectSlug, type BootstrapData, type Creative, type Hypothesis, type PlacementAspect, type WorkspaceEvent } from "@lovmia/shared";

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) { super(message); this.status = status; }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  // Content-Type ставим, только когда тело реально есть: на DELETE и POST-действия
  // без тела Fastify отвечает 400 (FST_ERR_CTP_EMPTY_JSON_BODY). FormData свой
  // boundary проставляет сам.
  const json = init?.body != null && !(init.body instanceof FormData);
  const response = await fetch(path, {
    credentials: "include",
    ...init,
    headers: json ? { "Content-Type": "application/json", ...init?.headers } : init?.headers
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ message: "Ошибка запроса" })) as { message?: string };
    throw new ApiError(body.message ?? `Request failed: ${response.status}`, response.status);
  }
  return await response.json() as T;
}

export const loadBootstrap = () => api<BootstrapData>("/api/bootstrap");

/** Полные записи для модалок: облегчённый bootstrap не несёт сценарии и шаблоны. */
export const loadCreative = (id: string) => api<Creative>(`/api/creatives/${encodeURIComponent(id)}`);
export const loadHypothesis = (id: string) => api<Hypothesis>(`/api/hypotheses/${encodeURIComponent(id)}`);

/** Хвост ленты после курсора — фолбэк живого потока и добор после сна вкладки. */
export const loadEvents = (since: number) =>
  api<{ events: WorkspaceEvent[]; cursor: number }>(`/api/events?since=${since}`);

/** Прогресс в долях 0..1; при неизвестной длине браузер их не отдаёт — тогда не зовём. */
export type UploadProgress = (fraction: number) => void;

/** Оригинал-исходник гипотезы — простой multipart: файл редкий и одноразовый. */
export async function uploadHypothesisSource(hypothesisId: string, file: File) {
  const body = new FormData();
  body.append("file", file);
  return await api(`/api/hypotheses/${hypothesisId}/source-video`, { method: "POST", body });
}

function putWithProgress(url: string, file: File, onProgress?: UploadProgress) {
  return new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("PUT", url, true);
    request.setRequestHeader("Content-Type", file.type);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total);
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) resolve();
      else reject(new ApiError(`Хранилище отклонило загрузку: ${request.status}`, request.status));
    };
    request.onerror = () => reject(new ApiError("Не удалось загрузить файл в хранилище", 0));
    request.onabort = () => reject(new ApiError("Загрузка отменена", 0));
    request.send(file);
  });
}

/**
 * Загрузка крео. В проде идёт мимо сервера: подписанный PUT прямо в бакет —
 * иначе видео до 500 МБ упирается в лимит тела у Cloudflare-прокси. Когда
 * хранилище локальное (дев), бекенд отвечает 409 и мы падаем на multipart.
 */
export async function uploadCreativeVideo(creativeId: string, file: File, onProgress?: UploadProgress) {
  try {
    const target = await api<{ fileId: string; storageKey: string; uploadUrl: string }>(
      `/api/creatives/${creativeId}/upload-url`,
      { method: "POST", body: JSON.stringify({ filename: file.name, mimeType: file.type, size: file.size }) }
    );
    await putWithProgress(target.uploadUrl, file, onProgress);
    return await api(`/api/creatives/${creativeId}/upload-complete`, {
      method: "POST",
      body: JSON.stringify({ fileId: target.fileId, storageKey: target.storageKey, originalName: file.name })
    });
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 409) throw error;
    const body = new FormData();
    body.append("file", file);
    return await api(`/api/creatives/${creativeId}/upload`, { method: "POST", body });
  }
}

/** Файл локали — тем же контуром, что крео: подписанный PUT в бакет, multipart-фолбэк локально. */
export async function uploadLocalizationVideo(localizationId: string, file: File, onProgress?: UploadProgress) {
  try {
    const target = await api<{ fileId: string; storageKey: string; uploadUrl: string }>(
      `/api/localizations/${localizationId}/upload-url`,
      { method: "POST", body: JSON.stringify({ filename: file.name, mimeType: file.type, size: file.size }) }
    );
    await putWithProgress(target.uploadUrl, file, onProgress);
    return await api(`/api/localizations/${localizationId}/upload-complete`, {
      method: "POST",
      body: JSON.stringify({ fileId: target.fileId, storageKey: target.storageKey, originalName: file.name })
    });
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 409) throw error;
    const body = new FormData();
    body.append("file", file);
    return await api(`/api/localizations/${localizationId}/upload`, { method: "POST", body });
  }
}

/**
 * Квадрат (или 4:5) под ленту того же объявления: та же дорога — подписанный
 * PUT в бакет, multipart-фолбэк локально. Пропорция в пути слагом `1x1`.
 */
export async function uploadPlacementAsset(subjectId: string, aspect: PlacementAspect, file: File, onProgress?: UploadProgress) {
  const base = `/api/placement-assets/${subjectId}/${placementAspectSlug(aspect)}`;
  try {
    const target = await api<{ fileId: string; storageKey: string; uploadUrl: string }>(
      `${base}/upload-url`,
      { method: "POST", body: JSON.stringify({ filename: file.name, mimeType: file.type, size: file.size }) }
    );
    await putWithProgress(target.uploadUrl, file, onProgress);
    return await api(`${base}/upload-complete`, {
      method: "POST",
      body: JSON.stringify({ fileId: target.fileId, storageKey: target.storageKey, originalName: file.name })
    });
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 409) throw error;
    const body = new FormData();
    body.append("file", file);
    return await api(`${base}/upload`, { method: "POST", body });
  }
}

/**
 * Лист персонажа. Дорога та же, что у видео (подписанный PUT мимо сервера,
 * multipart-фолбэк на локальном хранилище) — контур загрузки в приложении один,
 * и расходиться им незачем.
 */
export async function uploadCharacterImage(characterId: string, file: File, onProgress?: UploadProgress) {
  try {
    const target = await api<{ fileId: string; storageKey: string; uploadUrl: string }>(
      `/api/characters/${characterId}/image-url`,
      { method: "POST", body: JSON.stringify({ filename: file.name, mimeType: file.type, size: file.size }) }
    );
    await putWithProgress(target.uploadUrl, file, onProgress);
    return await api(`/api/characters/${characterId}/image-complete`, {
      method: "POST",
      body: JSON.stringify({ fileId: target.fileId, storageKey: target.storageKey, originalName: file.name })
    });
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 409) throw error;
    const body = new FormData();
    body.append("file", file);
    return await api(`/api/characters/${characterId}/image`, { method: "POST", body });
  }
}

export function formatMoney(value: number | null, digits = 0) {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: digits }).format(value);
}

export function formatPercent(value: number | null, digits = 1) {
  if (value == null) return "—";
  return `${(value * 100).toFixed(digits)}%`;
}

/** Дата без времени: «3 авг 2026». Для «когда это завели», а не «когда меняли». */
export function formatDate(value: string | null) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short", year: "numeric" }).format(new Date(value));
}

export function relativeTime(value: string | null) {
  if (!value) return "ещё не было";
  const minutes = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60_000));
  if (minutes < 1) return "только что";
  if (minutes < 60) return `${minutes} мин назад`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ч назад`;
  return `${Math.round(hours / 24)} дн назад`;
}
